import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { queryJobFilterCounts } from '@/features/jobs/data/jobs';
import { normaliseMetricsToolbarTagOptions } from '@/features/shared/components/metricsToolbar/metricsToolbarTagOptions';
import { buildCountedDropdownOptions } from '@/features/shared/components/tableSystem/filterOptionCounts';
import { queryKeys } from '@/shared/hooks';
import {
  buildDateRangeFilters,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import type { MetricsToolbarTagOption } from '@/features/shared/components/metricsToolbar/metricsToolbarTagOptions';
import type { TableDropdownOption } from '@/features/shared/components/tableSystem/tableToolbar.types';
import type { BuildStatus, JobListItemDto } from '@flowqa/shared';

type UseJobsPageFilterOptionCountsArgs = {
  currentJobs: readonly JobListItemDto[];
  effectiveProjectId: string | null;
  enabled: boolean;
  isAuthenticated: boolean;
  jobOptions: readonly TableDropdownOption[];
  jobSearchValue: string;
  pipelineOptions: readonly TableDropdownOption[];
  rangeEndIso: string | null;
  rangeStartIso: string | null;
  selectedEndDay: string | null;
  selectedJobNames: string[];
  selectedOrganisationId: string | null;
  selectedPipelineIds: string[];
  selectedStageNames: string[];
  selectedStartDay: string | null;
  selectedStatuses: BuildStatus[];
  selectedTags: string[];
  stageOptions: readonly TableDropdownOption[];
  tagOptions: readonly MetricsToolbarTagOption[];
};

type CountedJobFilterOptions = {
  pipelineOptions: TableDropdownOption[];
  stageOptions: TableDropdownOption[];
  jobOptions: TableDropdownOption[];
  tagOptions: MetricsToolbarTagOption[];
};

export const useJobsPageFilterOptionCounts = ({
  effectiveProjectId,
  enabled,
  isAuthenticated,
  jobOptions,
  jobSearchValue,
  pipelineOptions,
  rangeEndIso,
  rangeStartIso,
  selectedEndDay,
  selectedJobNames,
  selectedOrganisationId,
  selectedPipelineIds,
  selectedStageNames,
  selectedStartDay,
  selectedStatuses,
  selectedTags,
  stageOptions,
  tagOptions,
}: UseJobsPageFilterOptionCountsArgs): CountedJobFilterOptions => {
  const jobCountDateFilters = useMemo(
    () =>
      buildDateRangeFilters(
        'startedAt',
        selectedStartDay ? dayjs(selectedStartDay).startOf('day').toISOString() : rangeStartIso,
        selectedEndDay ? dayjs(selectedEndDay).endOf('day').toISOString() : rangeEndIso,
      ),
    [rangeEndIso, rangeStartIso, selectedEndDay, selectedStartDay],
  );
  const pipelineCountJobFilters = useMemo(
    () =>
      mergeFilters(
        buildInFilter('status', selectedStatuses),
        buildInFilter('stageName', selectedStageNames),
        buildInFilter('jobName', selectedJobNames),
        buildInFilter('tags', selectedTags),
        jobCountDateFilters,
      ),
    [jobCountDateFilters, selectedJobNames, selectedStageNames, selectedStatuses, selectedTags],
  );
  const stageCountJobFilters = useMemo(
    () =>
      mergeFilters(
        buildInFilter('status', selectedStatuses),
        buildInFilter('pipelineId', selectedPipelineIds),
        buildInFilter('jobName', selectedJobNames),
        buildInFilter('tags', selectedTags),
        jobCountDateFilters,
      ),
    [jobCountDateFilters, selectedJobNames, selectedPipelineIds, selectedStatuses, selectedTags],
  );
  const jobNameCountJobFilters = useMemo(
    () =>
      mergeFilters(
        buildInFilter('status', selectedStatuses),
        buildInFilter('pipelineId', selectedPipelineIds),
        buildInFilter('stageName', selectedStageNames),
        buildInFilter('tags', selectedTags),
        jobCountDateFilters,
      ),
    [jobCountDateFilters, selectedPipelineIds, selectedStageNames, selectedStatuses, selectedTags],
  );
  const tagCountJobFilters = useMemo(
    () =>
      mergeFilters(
        buildInFilter('status', selectedStatuses),
        buildInFilter('pipelineId', selectedPipelineIds),
        buildInFilter('stageName', selectedStageNames),
        buildInFilter('jobName', selectedJobNames),
        jobCountDateFilters,
      ),
    [
      jobCountDateFilters,
      selectedJobNames,
      selectedPipelineIds,
      selectedStageNames,
      selectedStatuses,
    ],
  );
  const countGroups = useMemo(
    () => ({
      pipelines: pipelineCountJobFilters,
      stages: stageCountJobFilters,
      jobs: jobNameCountJobFilters,
      tags: tagCountJobFilters,
    }),
    [jobNameCountJobFilters, pipelineCountJobFilters, stageCountJobFilters, tagCountJobFilters],
  );
  const filterCountsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'jobs/page',
      'filter-counts',
      selectedOrganisationId,
      effectiveProjectId,
      jobSearchValue,
      JSON.stringify(countGroups),
    ),
    enabled: isAuthenticated && enabled,
    queryFn: async () =>
      await queryJobFilterCounts(effectiveProjectId, selectedOrganisationId, {
        search: jobSearchValue,
        filters: countGroups,
      }),
  });

  return useMemo(
    () => ({
      pipelineOptions: buildCountedDropdownOptions(
        pipelineOptions,
        filterCountsQuery.data?.pipelines ?? {},
      ),
      stageOptions: buildCountedDropdownOptions(stageOptions, filterCountsQuery.data?.stages ?? {}),
      jobOptions: buildCountedDropdownOptions(jobOptions, filterCountsQuery.data?.jobs ?? {}),
      tagOptions: buildCountedDropdownOptions(
        normaliseMetricsToolbarTagOptions(tagOptions),
        filterCountsQuery.data?.tags ?? {},
      ),
    }),
    [
      filterCountsQuery.data?.jobs,
      filterCountsQuery.data?.pipelines,
      filterCountsQuery.data?.stages,
      filterCountsQuery.data?.tags,
      jobOptions,
      pipelineOptions,
      stageOptions,
      tagOptions,
    ],
  );
};
